import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import "./AdminDashboard.css"; // riusa i token e i .btn/.ad-card delle pagine admin
import "./AdminSiteNav.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const STATUS_URL = `${API_BASE}/api/admin/site-nav`; // GET  (admin) stato completo
const SYNC_URL = `${API_BASE}/api/admin/site-nav/sync`; // POST (admin) allineamento

const STORE_NAMES = { apple: "App Store", google: "Google Play" };

function fmtDate(v) {
  if (!v) return null;
  try {
    return new Date(v).toLocaleString("it-IT");
  } catch {
    return String(v);
  }
}

// last_error dal backend è tecnico ("fetch: ...", "validation: ..."):
// lo traduciamo per chi non è tecnico.
function friendlyLastError(raw) {
  if (!raw) return null;
  if (raw.startsWith("fetch:")) {
    return "l'ultima volta non è stato possibile leggere il sito.";
  }
  if (raw.startsWith("validation:")) {
    return "l'ultima volta il contenuto del sito non era nel formato atteso.";
  }
  return "l'ultimo tentativo non è andato a buon fine.";
}

export default function AdminSiteNav() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null); // riga da GET /api/admin/site-nav
  const [result, setResult] = useState(null); // { tone: "ok"|"warn", text, detail? }

  const loadStatus = useCallback(async () => {
    const r = await fetch(STATUS_URL, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const js = await loadStatus();
        if (alive) setStatus(js || {});
      } catch (e) {
        console.error(e);
        if (alive) toast.error("Impossibile caricare lo stato attuale");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadStatus]);

  async function handleSync() {
    if (syncing) return;
    setSyncing(true);
    setResult(null);
    try {
      const r = await fetch(SYNC_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const js = await r.json().catch(() => ({}));

      if (r.ok && js.ok) {
        setResult({
          tone: "ok",
          text: js.changed
            ? "Header e footer aggiornati dal sito."
            : "Già allineato, nessuna modifica.",
        });
      } else if (r.status === 502) {
        setResult({
          tone: "warn",
          text:
            "Non è stato possibile leggere il sito in questo momento. " +
            "I dati precedenti sono stati mantenuti.",
        });
      } else if (r.status === 422) {
        setResult({
          tone: "warn",
          text:
            "Il contenuto del sito non è nel formato atteso. " +
            "I dati precedenti sono stati mantenuti.",
        });
      } else {
        setResult({
          tone: "warn",
          text: "Qualcosa è andato storto durante l'allineamento. Riprova tra poco.",
        });
      }
    } catch (e) {
      console.error(e);
      setResult({
        tone: "warn",
        text: "Qualcosa è andato storto durante l'allineamento. Riprova tra poco.",
      });
    } finally {
      setSyncing(false);
      // ricarica lo stato (last_synced_at / last_error / anteprima)
      try {
        const js = await loadStatus();
        setStatus(js || {});
      } catch {
        /* niente: lo stato mostrato resta quello precedente */
      }
    }
  }

  const header = Array.isArray(status?.header_json) ? status.header_json : [];
  const footer =
    status?.footer_json && typeof status.footer_json === "object"
      ? status.footer_json
      : {};
  const footerCols = Array.isArray(footer.columns) ? footer.columns : [];
  const footerSocial = Array.isArray(footer.social) ? footer.social : [];
  // null = dato salvato prima dei badge (l'app usa quelli di riserva)
  const footerStores = Array.isArray(footer.stores) ? footer.stores : null;

  const lastSynced = fmtDate(status?.last_synced_at);
  const lastAttempt = fmtDate(status?.last_attempt_at);
  const lastErrorFriendly = friendlyLastError(status?.last_error);

  return (
    <div className="ad-wrap">
      <div className="ad-page">
        <h1 className="ad-title">Header &amp; Footer</h1>

        <p className="asn-intro">
          Le voci di menu in alto nell'app (su schermo grande) e il footer in
          fondo alle pagine vengono presi dai file del sito{" "}
          <strong>www.stirup.it</strong>. Quando sul sito cambi quelle voci,
          premi qui il pulsante per rileggerle e aggiornarle nell'app.
        </p>

        <section className="ad-card">
          <header className="ad-card-head">
            <h2 className="ad-card-title">Allinea dal sito</h2>
          </header>

          <div className="asn-status">
            {loading ? (
              "Caricamento stato…"
            ) : (
              <>
                <div>
                  Ultimo allineamento riuscito:{" "}
                  <strong>{lastSynced || "mai eseguito"}</strong>
                </div>
                {status?.last_error && (
                  <div className="asn-error-note">
                    Nota: {lastErrorFriendly}
                    {lastAttempt ? ` (tentativo del ${lastAttempt})` : ""} I dati
                    mostrati sotto sono comunque quelli validi più recenti.
                  </div>
                )}
              </>
            )}
          </div>

          <div className="ad-actions">
            <button
              type="button"
              className="btn btnPrimary"
              onClick={handleSync}
              disabled={syncing || loading}
              aria-busy={syncing}
            >
              {syncing ? "Allineamento in corso…" : "Allinea header e footer dal sito"}
            </button>
          </div>

          {result && (
            <div className={`asn-result ${result.tone === "ok" ? "is-ok" : "is-warn"}`}>
              {result.text}
              {status?.last_synced_at && (
                <small>Ultimo allineamento riuscito: {lastSynced}</small>
              )}
            </div>
          )}

          <p className="ad-hint">
            L'operazione legge due file pubblici del sito e non modifica il sito
            in alcun modo. Se qualcosa non va, l'app continua a mostrare le voci
            valide dell'ultimo allineamento riuscito.
          </p>
        </section>

        <section className="ad-card asn-preview-card">
          <header className="ad-card-head">
            <h2 className="ad-card-title">Cosa c'è ora nell'app</h2>
          </header>

          {loading ? (
            <div className="ad-loading">Caricamento…</div>
          ) : (
            <div className="asn-preview">
              <div>
                <h3>Menu header ({header.length})</h3>
                {header.length ? (
                  <div className="asn-chips">
                    {header.map((it, i) => (
                      <span className="asn-chip" key={it.url || i} title={it.url}>
                        {it.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="ad-empty">
                    Nessuna voce salvata: l'app sta usando le voci di riserva.
                  </div>
                )}
              </div>

              <div>
                <h3>Footer ({footerCols.length} colonne)</h3>
                {footerCols.length ? (
                  <div className="asn-fcols">
                    {footerCols.map((col, ci) => (
                      <div className="asn-fcol" key={col.title || ci}>
                        <h4>{col.title}</h4>
                        <ul>
                          {(col.items || []).map((it, ii) => (
                            <li key={it.url || `${ci}-${ii}`} title={it.url}>
                              {it.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ad-empty">
                    Nessuna colonna salvata: l'app sta usando il footer di riserva.
                  </div>
                )}

                {footerSocial.length > 0 && (
                  <div className="asn-social-line">
                    Social:{" "}
                    {footerSocial.map((s) => s.platform).filter(Boolean).join(", ")}
                  </div>
                )}

                {footerCols.length > 0 && (
                  <div className="asn-social-line">
                    Badge store (solo web):{" "}
                    {footerStores === null
                      ? "non ancora letti, premi il pulsante di allineamento"
                      : footerStores.length
                        ? footerStores
                            .map((s) => STORE_NAMES[s.store] || s.store || "altro")
                            .join(", ")
                        : "nessuno"}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
